/**
 * Strict Food Analysis Service
 * Uses Zod schema validation and enforces same-category alternatives
 */

import { callOpenAI } from './openaiService';
import type { ScanAnalysis } from './scanService';
import { getCachedAnalysis, saveCachedAnalysis } from '../utils/foodAnalysisCache';
import {
  FoodAnalysisResponse,
  validateFoodAnalysisResponse,
  safeValidateFoodAnalysisResponse,
  type FoodCategory
} from './foodAnalysisSchema';
import type { ChatGPTFoodAnalysis } from './foodAnalysisService';

/**
 * Generate unique request ID for logging
 */
function generateRequestId(): string {
  return `food_scan_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Normalize food name and ingredients for cache key
 */
function normalizeCacheKey(foodName: string, ingredientsText?: string): string {
  const normalizedName = foodName.trim().toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ');
  const normalizedIngredients = ingredientsText?.trim().toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ') || '';
  return `${normalizedName}|${normalizedIngredients}`;
}

/**
 * Extract JSON from response - strict extraction
 */
function extractJSONStrict(response: string): string | null {
  const trimmed = response.trim();
  
  // Remove markdown code blocks if present
  let cleaned = trimmed
    .replace(/```json\s*/g, '')
    .replace(/```javascript\s*/g, '')
    .replace(/```\s*/g, '');
  
  // Find first { and last }
  const firstBrace = cleaned.indexOf('{');
  const lastBrace = cleaned.lastIndexOf('}');
  
  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    return null;
  }
  
  return cleaned.substring(firstBrace, lastBrace + 1);
}

/**
 * Map new schema format to legacy format for UI compatibility
 */
function mapToLegacyFormat(response: FoodAnalysisResponse): ChatGPTFoodAnalysis {
  // Map acne impact to score (1-100) — deterministic values
  const scoreMap: Record<string, number> = {
    'low': 18,
    'medium': 55,
    'high': 82
  };
  
  const acneImpactScore = scoreMap[response.acneImpact] || 50;
  
  // Map to legacy category
  const categoryMap: Record<string, 'low' | 'moderate' | 'high'> = {
    'low': 'low',
    'medium': 'moderate',
    'high': 'high'
  };
  
  return {
    foodName: response.food,
    acneImpactScore,
    acneImpactCategory: categoryMap[response.acneImpact] || 'moderate',
    explanation: response.why.join(' '),
    triggers: response.warningIfAny || [],
    protectiveFactors: [],
    alternatives: response.betterAlternatives.map((alt, idx) => ({
      name: alt.name,
      description: alt.swapReason,
      acneImpactScore: Math.max(20, Math.min(100, acneImpactScore - 15 - idx * 5)),
      recipe: {
        title: alt.recipe.title,
        ingredients: alt.recipe.ingredients.map(ing => ({
          amount: typeof ing.amount === 'number' ? ing.amount.toString() : ing.amount,
          unit: ing.unit,
          name: ing.name,
          notes: ing.notes
        })),
        steps: alt.recipe.steps,
        servings: alt.recipe.servings,
        caloriesPerServing: alt.recipe.caloriesPerServing,
        cuisine: alt.recipe.cuisine,
        notes: alt.recipe.notes
      }
    })),
    brands: response.brands.map(brand => ({
      name: brand.name,
      description: brand.description,
      acneImpactScore: brand.acneImpactScore,
      whyHealthy: brand.whyHealthy
    }))
  };
}

/**
 * Create strict system prompt that enforces JSON-only output
 */
function createStrictSystemPrompt(): string {
  return `You are a nutrition expert analyzing foods for acne impact. Return ONLY valid JSON, no markdown, no code blocks, no explanations.

REQUIRED JSON SCHEMA:
{
  "food": "string (exact food name)",
  "category": "Frozen dessert|Dairy dessert|Baked sweet|Savory snack|Fast food|Beverage|Meal|Candy|Fruit|Vegetable|Protein|Other",
  "acneImpact": "low|medium|high",
  "why": ["reason1", "reason2", "reason3"],
  "betterAlternatives": [
    {
      "name": "string (alternative food name)",
      "sameCategory": true,
      "swapReason": "string (why this is better for skin)",
      "recipe": {
        "title": "string (recipe title)",
        "ingredients": [
          {"amount": number or string, "unit": "string", "name": "string", "notes": "optional string"}
        ],
        "steps": ["step1", "step2", "step3..."],
        "servings": number,
        "caloriesPerServing": number,
        "cuisine": "optional string",
        "notes": "optional string"
      }
    }
  ],
  "brands": [
    {
      "name": "string (brand name)",
      "description": "string (what this brand offers)",
      "acneImpactScore": number (1-100, lower is better),
      "whyHealthy": "string (why this brand is healthier for skin)"
    }
  ],
  "portionTips": ["tip1", "tip2"],
  "warningIfAny": ["warning1", "warning2"]
}

CRITICAL RULES:
1. Return ONLY the JSON object, nothing else
2. Classify the input food into ONE category from the enum
3. ALL alternatives MUST be in the SAME category as the input (sameCategory must be true)
4. ALWAYS provide 3-6 alternatives, even if the food has low acne impact — suggest even healthier or equally skin-friendly options
5. Each alternative MUST include a complete recipe with a full ingredients list (with amounts, units, and notes), steps, servings, and calories
6. Provide 2-5 healthier snack brands specific to this food category (e.g., if input is ice cream, suggest healthy ice cream brands)
7. Do NOT suggest unrelated foods (e.g., if input is ice cream, do NOT suggest salad or broccoli)
8. Alternatives must be foods a user would actually choose as substitutes
9. Recipes should be practical and easy to follow
10. Every recipe MUST have a detailed ingredients list — never omit ingredients

Return ONLY JSON, no markdown code blocks, no text before/after.`;
}

/**
 * Analyze food with strict JSON schema validation
 */
export async function analyzeFoodWithChatGPTStrict(
  foodName: string,
  ingredientsText?: string,
  scanAnalysis?: ScanAnalysis | null,
  dietaryPreference?: 'vegetarian' | 'non-vegetarian'
): Promise<ChatGPTFoodAnalysis> {
  const requestId = generateRequestId();
  const startTime = Date.now();
  
  // Normalize for cache key
  const cacheKey = normalizeCacheKey(foodName, ingredientsText);
  
  // Check cache first
  const cached = getCachedAnalysis(foodName, ingredientsText);
  if (cached) {
    console.log(`[${requestId}] Cache HIT for: ${foodName}`, { cacheKey });
    return cached;
  }
  
  console.log(`[${requestId}] Cache MISS, analyzing: ${foodName}`, { cacheKey, ingredientsText });

  // Create user message
  const userMessage = `Analyze this food for acne impact:
Food: ${foodName}
${ingredientsText ? `Ingredients: ${ingredientsText}` : ''}

REQUIREMENTS:
1. Classify into ONE category from the enum
2. ALWAYS provide 3-6 alternatives in the SAME category, even if the food is healthy or has low acne impact — suggest equally healthy or even better options
3. Each alternative MUST include a complete recipe with a full ingredients list (amounts, units, names), steps, servings, and calories
4. Provide 2-5 healthier snack brands specific to this food category
5. All alternatives must have sameCategory: true
6. Recipes must be practical and easy to follow with detailed ingredient lists
7. Never return an empty betterAlternatives array

Return ONLY JSON.`;
  
  const systemPrompt = createStrictSystemPrompt();
  
  try {
    // Make API call
    console.log(`[${requestId}] Calling OpenAI API...`);
    const response = await callOpenAI(systemPrompt, userMessage);
    
    const apiLatency = Date.now() - startTime;
    console.log(`[${requestId}] API response received (${apiLatency}ms)`, { responseLength: response.length });
    
    // Extract JSON strictly
    const jsonString = extractJSONStrict(response);
    
    if (!jsonString) {
      console.error(`[${requestId}] No JSON found in response`, { responsePreview: response.substring(0, 500) });
      throw new Error('No valid JSON found in API response');
    }
    
    // Parse JSON
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonString);
      console.log(`[${requestId}] JSON parsed successfully`);
    } catch (parseError: any) {
      console.error(`[${requestId}] JSON parse error:`, parseError.message, { jsonString: jsonString.substring(0, 500) });
      throw new Error('Failed to parse API response as JSON');
    }
    
    // Validate with Zod schema
    const validated = safeValidateFoodAnalysisResponse(parsed);
    
    if (!validated) {
      console.error(`[${requestId}] Schema validation failed`, { parsed });
      throw new Error('API response did not match expected schema');
    }
    
    // Verify all alternatives have sameCategory=true
    const allSameCategory = validated.betterAlternatives.every(alt => alt.sameCategory === true);
    if (!allSameCategory) {
      console.warn(`[${requestId}] Some alternatives missing sameCategory=true, filtering...`);
      validated.betterAlternatives = validated.betterAlternatives.filter(alt => alt.sameCategory === true);
    }
    
    // Verify all alternatives have recipes
    const allHaveRecipes = validated.betterAlternatives.every(alt => alt.recipe);
    if (!allHaveRecipes) {
      console.warn(`[${requestId}] Some alternatives missing recipes, filtering...`);
      validated.betterAlternatives = validated.betterAlternatives.filter(alt => alt.recipe);
    }
    
    // Verify we have brands
    if (!validated.brands || validated.brands.length === 0) {
      console.warn(`[${requestId}] No brands provided, this may affect user experience`);
    }
    
    // Verify alternatives are in the same category (semantic check)
    // This is a best-effort check - the prompt should handle this
    console.log(`[${requestId}] Validation successful`, {
      food: validated.food,
      category: validated.category,
      alternativesCount: validated.betterAlternatives.length,
      brandsCount: validated.brands?.length || 0,
      allSameCategory,
      allHaveRecipes
    });
    
    // Map to legacy format
    const legacyFormat = mapToLegacyFormat(validated);
    
    // Cache result
    saveCachedAnalysis(foodName, legacyFormat, ingredientsText);
    
    const totalLatency = Date.now() - startTime;
    console.log(`[${requestId}] Analysis complete (${totalLatency}ms)`, {
      apiLatency,
      parseLatency: totalLatency - apiLatency,
      cacheKey
    });
    
    return legacyFormat;
    
  } catch (error: any) {
    const totalLatency = Date.now() - startTime;
    console.error(`[${requestId}] Analysis failed (${totalLatency}ms):`, {
      error: error.message,
      status: error.status
    });
    
    // Re-throw so the caller's local fallback can run
    throw error;
  }
}

